import { useRef, useState } from 'react';
import { chargeDetailsProblems, type ChargeDraft, type ChargeDraftProblem } from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Icon } from '@mypoz/ui';

import type { ChargeFields } from '@/features/china-payment/chargeFields.js';
import { FormField } from '@/features/china-payment/components/FormField.js';
import { buttonClass, inputClass } from '@/lib/styles.js';

/** The problems this step asks about — each has its own message. */
type DetailsProblem = Extract<
  ChargeDraftProblem,
  | 'name'
  | 'idNumber'
  | 'phone'
  | 'payerAccount'
  | 'recipientName'
  | 'recipientCardNumber'
  | 'recipientBank'
>;

export interface DetailsStepProps {
  draft: ChargeDraft;
  fields: ChargeFields;
  onChange: (patch: Partial<ChargeFields>) => void;
  onBack: () => void;
  onNext: () => void;
}

/**
 * Step 2 of a China Payment request (MYP-109), the old website's 充值账号信息: who is asking — name,
 * IC / passport number and phone, filled in from the account — and what the request is for: the
 * Alipay account or WeChat ID to top up, the Taobao account and seller (proxy pay), or the China
 * recipient (bank transfer).
 *
 * The old website also asks for that Alipay / WeChat account's **login password** (登入密码), and
 * proxy pay's Taobao password. The app never does: there is no field for one anywhere in the
 * request (`ChargeDraft`), so none can be sent, logged or kept.
 */
export function DetailsStep({ draft, fields, onChange, onBack, onNext }: DetailsStepProps) {
  const { t } = useTranslation();
  const [tried, setTried] = useState(false);
  const [showId, setShowId] = useState(false);
  const form = useRef<HTMLDivElement>(null);
  const problems = chargeDetailsProblems(draft);
  const error = (key: DetailsProblem) =>
    tried && problems.includes(key) ? t(`chinaPayment.form.problem.${key}`) : undefined;
  const type = draft.type;

  const next = () => {
    setTried(true);
    if (problems.length > 0) {
      // To the first thing still missing, once React has marked it.
      requestAnimationFrame(() =>
        form.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus(),
      );
      return;
    }
    onNext();
  };

  return (
    <div ref={form} className="grid gap-4">
      <p className="text-xs text-muted">{t('chinaPayment.form.prefilled')}</p>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t('chinaPayment.form.name')} error={error('name')}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              value={fields.name}
              onChange={(event) => onChange({ name: event.target.value })}
              aria-invalid={invalid || undefined}
              aria-describedby={describedBy}
              autoComplete="name"
              className={inputClass()}
            />
          )}
        </FormField>
        <FormField label={t('chinaPayment.form.phone')} error={error('phone')}>
          {({ id, describedBy, invalid }) => (
            <input
              id={id}
              type="tel"
              value={fields.phone}
              onChange={(event) => onChange({ phone: event.target.value })}
              aria-invalid={invalid || undefined}
              aria-describedby={describedBy}
              autoComplete="tel"
              className={inputClass('tabular-nums')}
            />
          )}
        </FormField>
      </div>

      <FormField label={t('chinaPayment.form.idNumber')} error={error('idNumber')}>
        {({ id, describedBy, invalid }) => (
          <div className="flex gap-2">
            {/* Hidden like a password until asked for: it is someone's NRIC or passport number. */}
            <input
              id={id}
              type={showId ? 'text' : 'password'}
              value={fields.idNumber}
              onChange={(event) => onChange({ idNumber: event.target.value })}
              aria-invalid={invalid || undefined}
              aria-describedby={describedBy}
              autoComplete="off"
              className={inputClass('tabular-nums')}
            />
            <button
              type="button"
              onClick={() => setShowId((current) => !current)}
              aria-pressed={showId}
              aria-label={`${showId ? t('chinaPayment.form.hide') : t('chinaPayment.form.show')} — ${t('chinaPayment.form.idNumber')}`}
              className={buttonClass('secondary', 'shrink-0')}
            >
              {showId ? t('chinaPayment.form.hide') : t('chinaPayment.form.show')}
            </button>
          </div>
        )}
      </FormField>

      {type === 'BANKTRANS' ? (
        <>
          <FormField label={t('chinaPayment.form.recipientName')} error={error('recipientName')}>
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                value={fields.recipientName}
                onChange={(event) => onChange({ recipientName: event.target.value })}
                aria-invalid={invalid || undefined}
                aria-describedby={describedBy}
                autoComplete="off"
                className={inputClass()}
              />
            )}
          </FormField>
          <FormField
            label={t('chinaPayment.form.recipientCardNumber')}
            error={error('recipientCardNumber')}
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                inputMode="numeric"
                value={fields.recipientCardNumber}
                onChange={(event) => onChange({ recipientCardNumber: event.target.value })}
                aria-invalid={invalid || undefined}
                aria-describedby={describedBy}
                autoComplete="off"
                className={inputClass('tabular-nums')}
              />
            )}
          </FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label={t('chinaPayment.form.recipientBank')} error={error('recipientBank')}>
              {({ id, describedBy, invalid }) => (
                <input
                  id={id}
                  value={fields.recipientBank}
                  onChange={(event) => onChange({ recipientBank: event.target.value })}
                  aria-invalid={invalid || undefined}
                  aria-describedby={describedBy}
                  autoComplete="off"
                  className={inputClass()}
                />
              )}
            </FormField>
            <FormField label={t('chinaPayment.form.recipientBranch')}>
              {({ id }) => (
                <input
                  id={id}
                  value={fields.recipientBranch}
                  onChange={(event) => onChange({ recipientBranch: event.target.value })}
                  autoComplete="off"
                  className={inputClass()}
                />
              )}
            </FormField>
          </div>
        </>
      ) : (
        <>
          <FormField
            label={t(`chinaPayment.form.payerAccount.${type}`)}
            error={error('payerAccount')}
          >
            {({ id, describedBy, invalid }) => (
              <input
                id={id}
                value={fields.payerAccount}
                onChange={(event) => onChange({ payerAccount: event.target.value })}
                aria-invalid={invalid || undefined}
                aria-describedby={describedBy}
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                className={inputClass()}
              />
            )}
          </FormField>
          {type === 'ALIPAY' ? (
            <FormField label={t('chinaPayment.form.sellerName')}>
              {({ id }) => (
                <input
                  id={id}
                  value={fields.sellerName}
                  onChange={(event) => onChange({ sellerName: event.target.value })}
                  autoComplete="off"
                  className={inputClass()}
                />
              )}
            </FormField>
          ) : null}
        </>
      )}

      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
        <button type="button" onClick={onBack} className={buttonClass('secondary', 'px-5')}>
          <Icon name="arrow_back" size={18} />
          {t('chinaPayment.form.back')}
        </button>
        <button type="button" onClick={next} className={buttonClass('primary', 'px-6')}>
          {t('chinaPayment.form.next')}
          <Icon name="arrow_forward" size={18} />
        </button>
      </div>
    </div>
  );
}
